import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    Grid,
    CircularProgress,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSetupMutation } from '../api/JsdhNotifikaceService/service/SetupMutation';
import type { SetupRequest } from '../api/JsdhNotifikaceService/generated';

const steps = ['Administrátor', 'Jednotka', 'Adresa'];

interface FormData {
    // Step 1: Admin
    adminJmeno: string;
    adminTelefon: string;
    adminEmail: string;
    adminPassword: string;
    adminPasswordConfirm: string;
    // Step 2: Unit
    jednotkaNazev: string;
    jednotkaEvidencniCislo: string;
    // Step 3: Address
    adresaObec: string;
    adresaUlice: string;
    adresaCisloPopisne: string;
    adresaPsc: string;
}

const initialFormData: FormData = {
    adminJmeno: '',
    adminTelefon: '',
    adminEmail: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    jednotkaNazev: '',
    jednotkaEvidencniCislo: '',
    adresaObec: '',
    adresaUlice: '',
    adresaCisloPopisne: '',
    adresaPsc: '',
};

const SetupWizardPage: React.FC = () => {
    const navigate = useNavigate();
    const setupMutation = useSetupMutation();

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

    const handleChange = (field: keyof FormData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        // Clear error when user types
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const validateStep = (step: number): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (step === 0) {
            if (!formData.adminJmeno.trim()) {
                newErrors.adminJmeno = 'Jméno je povinné';
            }
            if (!formData.adminTelefon.trim()) {
                newErrors.adminTelefon = 'Telefon je povinný';
            } else if (!/^\d{9}$/.test(formData.adminTelefon)) {
                newErrors.adminTelefon = 'Telefon musí mít 9 číslic';
            }
            if (!formData.adminEmail.trim()) {
                newErrors.adminEmail = 'Email je povinný';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
                newErrors.adminEmail = 'Neplatný formát emailu';
            }
            if (!formData.adminPassword) {
                newErrors.adminPassword = 'Heslo je povinné';
            } else if (formData.adminPassword.length < 8) {
                newErrors.adminPassword = 'Heslo musí mít alespoň 8 znaků';
            }
            if (!formData.adminPasswordConfirm) {
                newErrors.adminPasswordConfirm = 'Potvrzení hesla je povinné';
            } else if (formData.adminPassword !== formData.adminPasswordConfirm) {
                newErrors.adminPasswordConfirm = 'Hesla se neshodují';
            }
        }

        if (step === 1) {
            if (!formData.jednotkaNazev.trim()) {
                newErrors.jednotkaNazev = 'Název jednotky je povinný';
            }
            if (!formData.jednotkaEvidencniCislo.trim()) {
                newErrors.jednotkaEvidencniCislo = 'Evidenční číslo je povinné';
            }
        }

        if (step === 2) {
            if (!formData.adresaObec.trim()) {
                newErrors.adresaObec = 'Obec je povinná';
            }
            if (!formData.adresaUlice.trim()) {
                newErrors.adresaUlice = 'Ulice je povinná';
            }
            if (!formData.adresaCisloPopisne.trim()) {
                newErrors.adresaCisloPopisne = 'Číslo popisné je povinné';
            }
            if (!formData.adresaPsc.trim()) {
                newErrors.adresaPsc = 'PSČ je povinné';
            } else if (!/^\d{5}$/.test(formData.adresaPsc.replace(/\s/g, ''))) {
                newErrors.adresaPsc = 'PSČ musí mít 5 číslic';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = () => {
        if (!validateStep(activeStep)) return;

        const request: SetupRequest = {
            adminJmeno: formData.adminJmeno.trim(),
            adminTelefon: parseInt(formData.adminTelefon, 10),
            adminEmail: formData.adminEmail.trim(),
            adminPassword: formData.adminPassword,
            jednotkaNazev: formData.jednotkaNazev.trim(),
            jednotkaEvidencniCislo: formData.jednotkaEvidencniCislo.trim(),
            adresaObec: formData.adresaObec.trim(),
            adresaUlice: formData.adresaUlice.trim(),
            adresaCisloPopisne: formData.adresaCisloPopisne.trim(),
            adresaPsc: formData.adresaPsc.replace(/\s/g, '').trim(),
        };

        setupMutation.mutate(request, {
            onSuccess: () => {
                navigate('/', { replace: true });
            },
        });
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Údaje administrátora
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Jméno a příjmení"
                                value={formData.adminJmeno}
                                onChange={handleChange('adminJmeno')}
                                error={!!errors.adminJmeno}
                                helperText={errors.adminJmeno}
                                autoFocus
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.adminEmail}
                                onChange={handleChange('adminEmail')}
                                error={!!errors.adminEmail}
                                helperText={errors.adminEmail}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Telefon"
                                value={formData.adminTelefon}
                                onChange={handleChange('adminTelefon')}
                                error={!!errors.adminTelefon}
                                helperText={errors.adminTelefon || 'Bez předvolby (např. 721234567)'}
                                inputProps={{ maxLength: 9 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Heslo"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.adminPassword}
                                onChange={handleChange('adminPassword')}
                                error={!!errors.adminPassword}
                                helperText={errors.adminPassword || 'Minimálně 8 znaků'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Potvrzení hesla"
                                type={showPasswordConfirm ? 'text' : 'password'}
                                value={formData.adminPasswordConfirm}
                                onChange={handleChange('adminPasswordConfirm')}
                                error={!!errors.adminPasswordConfirm}
                                helperText={errors.adminPasswordConfirm}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                                                edge="end"
                                            >
                                                {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                );

            case 1:
                return (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Údaje o jednotce
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Název jednotky"
                                value={formData.jednotkaNazev}
                                onChange={handleChange('jednotkaNazev')}
                                error={!!errors.jednotkaNazev}
                                helperText={errors.jednotkaNazev || 'Např. JSDH Obec'}
                                autoFocus
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                fullWidth
                                label="Evidenční číslo"
                                value={formData.jednotkaEvidencniCislo}
                                onChange={handleChange('jednotkaEvidencniCislo')}
                                error={!!errors.jednotkaEvidencniCislo}
                                helperText={errors.jednotkaEvidencniCislo || 'Číslo z registru PO (např. 550311-01)'}
                                placeholder="např. 550311-01"
                            />
                        </Grid>
                    </Grid>
                );

            case 2:
                return (
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                Adresa jednotky
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Obec"
                                value={formData.adresaObec}
                                onChange={handleChange('adresaObec')}
                                error={!!errors.adresaObec}
                                helperText={errors.adresaObec}
                                autoFocus
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="PSČ"
                                value={formData.adresaPsc}
                                onChange={handleChange('adresaPsc')}
                                error={!!errors.adresaPsc}
                                helperText={errors.adresaPsc || 'Např. 74212'}
                                inputProps={{ maxLength: 6 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8 }}>
                            <TextField
                                fullWidth
                                label="Ulice"
                                value={formData.adresaUlice}
                                onChange={handleChange('adresaUlice')}
                                error={!!errors.adresaUlice}
                                helperText={errors.adresaUlice}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                fullWidth
                                label="Číslo popisné"
                                value={formData.adresaCisloPopisne}
                                onChange={handleChange('adresaCisloPopisne')}
                                error={!!errors.adresaCisloPopisne}
                                helperText={errors.adresaCisloPopisne}
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box textAlign="center" mb={4}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Nastavení aplikace
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Hasiči Online - Počáteční konfigurace
                    </Typography>
                </Box>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {setupMutation.isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {setupMutation.error?.message || 'Nastala chyba při ukládání nastavení.'}
                    </Alert>
                )}

                <Box sx={{ minHeight: 280 }}>
                    {renderStepContent(activeStep)}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                        disabled={activeStep === 0 || setupMutation.isPending}
                        onClick={handleBack}
                    >
                        Zpět
                    </Button>

                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={setupMutation.isPending}
                        >
                            {setupMutation.isPending ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Dokončit nastavení'
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                        >
                            Další
                        </Button>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default SetupWizardPage;
